import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { wrapTaskNavigatorScreen } from '@euler/app/flows/task/wrapTaskNavigatorScreen';
import { Center } from '@euler/components/Center';
import { ErrorFallback } from '@euler/components/error';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import {
  createStackNavigator,
  TransitionPresets,
} from '@react-navigation/stack';
import { useTheme } from 'styled-components';
import { ConstructionJobScheduleContextProvider } from './ConstructionJobScheduleContext';
import { useConstructionWorkItems } from './functions/useConstructionWorkItems';
import { ScheduleJobsScreen } from './ScheduleJobsScreen';
import { WorkItemSearchScreen } from './WorkItemSearchScreen';

const Stack = createStackNavigator();

const Screens = {
  _workItems: ScheduleJobsScreen,
  _workItemSearch: WorkItemSearchScreen,
};

export type ScheduleConstructionJobNavParams = ScreenToParams<typeof Screens>;

export const ScheduleConstructionJobFlow = wrapTaskNavigatorScreen(
  () => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const {
      predefinedConstructionItemsInventoryError: error,
      workItemInventory: inventory,
      fetchPredefinedWorkItemsInventory,
      addCustomWorkItem,
      removeCustomWorkItem,
      clearCustomWorkItems,
    } = useConstructionWorkItems();

    if (inventory == null) {
      return error ? (
        <ErrorFallback
          error={error}
          resetError={fetchPredefinedWorkItemsInventory}
        />
      ) : (
        <Center
          css={`
            flex: 1;
          `}
        >
          <DefaultLoader />
        </Center>
      );
    }

    return (
      <ConstructionJobScheduleContextProvider
        categories={inventory.categories}
        addCustomWorkItem={addCustomWorkItem}
        removeCustomWorkItem={removeCustomWorkItem}
        clearCustomWorkItems={clearCustomWorkItems}
      >
        <Stack.Navigator
          initialRouteName="_workItems"
          screenOptions={{
            ...TransitionPresets.SlideFromRightIOS,
            headerMode: 'float',
            headerTitleAlign: 'center',
            headerBackTitle: ' ',
            headerTitleStyle: {
              fontSize: rpx(18),
              fontFamily: FontFamily.NotoSans.Bold,
            },
            cardStyle: {
              backgroundColor: theme.page.card.backgroundColor,
            },
            headerTitle: MultilineHeaderTitle,
            headerBackImage: () => <BackIcon />,
          }}
        >
          {spreadScreens(Stack.Screen, Screens, '_workItems')}
        </Stack.Navigator>
      </ConstructionJobScheduleContextProvider>
    );
  },
  {
    headerShown: false,
  },
  { dismissible: false, aside: true },
);
